import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { COLORS } from '../../styles/colors';
import { useListingsFeedContext } from '../../context/ListingsFeedContext';
import { useAuth } from '../../context/AuthContext';
import { useTier } from '../../context/TierContext';
import { useResponsive } from '../../hooks/useResponsive';
import supabase from '../../utils/supabaseClient';
import logger from '../../utils/logger';
import { trackConversion } from '../../utils/gtmHelpers';
import SkeletonPlaceholder from '../ui/SkeletonPlaceholder';
import ListingCard from './ListingCard';
import ListingDetailPopup from './ListingDetailPopup';
import FeedCapCTA from '../feed/FeedCapCTA';
import { UNDO_TOAST_DURATION_MS } from '../../config/listingsConfig';
const SKELETON_COUNT = 3;
// D-016: Free tier display cap for car-detail listings carousel.
// Same constant as UnifiedFeedScreen — single source of truth.
const FREE_DISPLAY_CAP = 10;
// Trial-tier: first N positions are unlocked, rest render as locked teasers.
// Matches TRIAL_UNLOCKED_PER_MODEL in UnifiedFeedScreen (single-model surface).
const TRIAL_UNLOCKED_COUNT = 2;
// Trial-tier defensive ceiling on this carousel. Same as TRIAL_DISPLAY_CAP in
// UnifiedFeedScreen so the locked-teaser tail has a consistent upper bound.
const TRIAL_DISPLAY_CAP = 24;

// ============================================================
// Sub-components
// ============================================================

/** Skeleton card shown during loading state. */
const SkeletonCard = memo(({ isMobile, screenWidth }) => {
  const cardWidth = isMobile ? screenWidth - 32 : 360;
  const imageHeight = isMobile ? 180 : 200;

  return (
    <View style={[skeletonStyles.card, { width: cardWidth, maxWidth: 400 }]}>
      <SkeletonPlaceholder width="100%" height={imageHeight} style={{ borderRadius: 0 }} />
      <View style={skeletonStyles.content}>
        <View style={skeletonStyles.badgeRow}>
          <SkeletonPlaceholder width={80} height={22} style={{ borderRadius: 6 }} />
          <SkeletonPlaceholder width={110} height={22} style={{ borderRadius: 6 }} />
        </View>
        <SkeletonPlaceholder width="75%" height={20} style={{ marginBottom: 8, borderRadius: 4 }} />
        <SkeletonPlaceholder width="55%" height={16} style={{ marginBottom: 6, borderRadius: 4 }} />
        <SkeletonPlaceholder width="40%" height={16} style={{ marginBottom: 12, borderRadius: 4 }} />
        <SkeletonPlaceholder width="30%" height={24} style={{ marginBottom: 12, borderRadius: 4 }} />
        <View style={skeletonStyles.footer}>
          <SkeletonPlaceholder width={80} height={24} style={{ borderRadius: 6 }} />
          <SkeletonPlaceholder width={120} height={32} style={{ borderRadius: 8 }} />
        </View>
      </View>
    </View>
  );
});
SkeletonCard.displayName = 'SkeletonCard';

const skeletonStyles = StyleSheet.create({
  card: {
    backgroundColor: COLORS.white,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: COLORS.borderColor,
    overflow: 'hidden',
    boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)',
    marginRight: 16,
  },
  content: {
    padding: 14,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 10,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: COLORS.borderColor,
    paddingTop: 12,
  },
});

/** Undo toast shown after dismissing a listing. */
const UndoToast = memo(({ onUndo }) => (
  <Pressable style={styles.undoToast} onPress={onUndo} accessibilityRole="button" accessibilityLabel="Undo dismiss">
    <Icon name="undo" size={18} color={COLORS.white} />
    <Text style={styles.undoToastText}>Listing dismissed</Text>
    <View style={styles.undoButton}>
      <Text style={styles.undoButtonText}>Undo</Text>
    </View>
  </Pressable>
));
UndoToast.displayName = 'UndoToast';

/** Empty state when no listings are available. */
const EmptyState = memo(({ vehicleModel, onNotifyMe, notifyConfirmed }) => (
  <View style={styles.emptyState}>
    <View style={styles.emptyIconContainer}>
      <Icon name="car-search" size={48} color="rgba(255, 255, 255, 0.3)" />
    </View>
    <Text style={styles.emptyTitle}>
      No {vehicleModel || 'matching'} recommendations yet. We're on it.
    </Text>
    <Text style={styles.emptySubtitle}>
      We scan daily and update your feed automatically.
    </Text>
    {notifyConfirmed ? (
      <View style={styles.notifyConfirmed}>
        <Icon name="check-circle" size={18} color="#16A34A" />
        <Text style={styles.notifyConfirmedText}>We'll email you when new listings appear</Text>
      </View>
    ) : (
      <Pressable
        style={styles.notifyMeButton}
        onPress={onNotifyMe}
        accessibilityRole="button"
        accessibilityLabel="Notify me when listings are available"
      >
        <Icon name="bell-outline" size={18} color={COLORS.white} />
        <Text style={styles.notifyMeText}>Notify me</Text>
      </Pressable>
    )}
  </View>
));
EmptyState.displayName = 'EmptyState';

/** Refreshing overlay shown when feed is stale and regenerating. */
const RefreshingOverlay = memo(() => (
  <View style={styles.refreshingOverlay}>
    <ActivityIndicator size="large" color={COLORS.primary} />
    <Text style={styles.refreshingText}>Refreshing your recommendations...</Text>
    <Text style={styles.refreshingSubtext}>This should only take a moment</Text>
  </View>
));
RefreshingOverlay.displayName = 'RefreshingOverlay';

/** Section header for the listings feed. */
const FeedHeader = memo(({ vehicleModel, listingCount }) => (
  <View style={styles.headerContainer}>
    <View style={styles.headerLeft}>
      <Icon name="car-multiple" size={22} color="rgba(255, 255, 255, 0.6)" />
      <Text style={styles.headerTitle}>
        {vehicleModel ? `${vehicleModel} Listings` : 'Live Listings'}
      </Text>
    </View>
    {listingCount > 0 && (
      <Text style={styles.headerCount}>
        Top {listingCount} {listingCount === 1 ? 'listing' : 'listings'}
      </Text>
    )}
  </View>
));
FeedHeader.displayName = 'FeedHeader';

// ============================================================
// Main component
// ============================================================

/**
 * ListingsFeed - Horizontal scrollable listing feed container.
 *
 * Displays daily listing feed cards in a horizontal FlatList with
 * snap-to-card behavior, dismiss/undo mechanism, save interactions,
 * and loading/empty states.
 *
 * @param {Object}   props
 * @param {string}   props.modelId       - The vehicle model ID
 * @param {string}   [props.vehicleModel] - Display name of the vehicle model (e.g. "BMW 3 Series")
 * @param {number}   [props.fitScore]    - Model-level fit score from user_model_fit_scores (0-100)
 * @param {string}   [props.vehicleModelImageUrl] - AI-generated vehicle model image URL (fallback for sold listings)
 */
const ListingsFeed = ({ modelId, vehicleModel, fitScore, vehicleModelImageUrl }) => {
  const { isMobile, screenWidth } = useResponsive();
  const posthog = usePostHog();
  const router = useRouter();
  const { user } = useAuth();

  // Context provides feed data and interaction handlers
  const {
    listings,
    noLongerAvailableListings,
    isLoading,
    isEmpty,
    feedGeneratedAt,
    topMatchIds,
    bestPriceIds,
    savingIds,
    dismissingIds,
    handleSave,
    handleDismiss,
    handleView,
    error,
  } = useListingsFeedContext();

  // D-016: tier access for cap enforcement. Trial is gated on subscriptionStatus
  // first because hasProAccess is true for trial_active (matches the /home
  // effectiveTier derivation in UnifiedFeedScreen).
  const { hasProAccess, subscriptionStatus, tier } = useTier();
  const isAdmin = tier === 'Admin';
  const isTrial = !isAdmin && subscriptionStatus === 'trial_active';

  // ---- Popup state ----
  const [selectedListing, setSelectedListing] = useState(null);

  // ---- FlatList navigation ----
  const flatListRef = useRef(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  // ---- Dismiss undo state ----
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [undoListingId, setUndoListingId] = useState(null);
  const undoTimerRef = useRef(null);
  const toastOpacity = useRef(new Animated.Value(0)).current;

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }
    };
  }, []);

  // ---- Visible listings (filter out optimistically dismissed ones) ----
  // D-016: apply FREE_DISPLAY_CAP for Free users on this carousel surface.
  const allVisibleListings = useMemo(() => {
    if (!listings) return [];
    return listings.filter((l) => !dismissedIds.has(l.id));
  }, [listings, dismissedIds]);

  const visibleListings = useMemo(() => {
    // Trial check must come before hasProAccess because trial_active grants
    // hasProAccess=true; without this order Trial users would see uncapped data.
    if (isTrial) return allVisibleListings.slice(0, TRIAL_DISPLAY_CAP);
    if (hasProAccess) return allVisibleListings;
    // Free: legacy cap (server-side cap_config.feed_listings.Free=0 post-pivot
    // means Free users normally don't reach this surface; kept defensively).
    return allVisibleListings.slice(0, FREE_DISPLAY_CAP);
  }, [allVisibleListings, hasProAccess, isTrial]);

  // D-016: total count (before cap) for the "N more on Pro" footer card
  const cappedTotalCount = allVisibleListings.length;

  // ---- Card width for snap behavior ----
  const cardWidth = useMemo(() => {
    const width = isMobile ? screenWidth - 32 : 360;
    return Math.min(width, 400);
  }, [isMobile, screenWidth]);

  const snapInterval = cardWidth + 16; // card width + gap

  // ---- Handlers ----

  /** Show undo toast with animation. */
  const showUndoToast = useCallback(() => {
    Animated.timing(toastOpacity, {
      toValue: 1,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [toastOpacity]);

  /** Hide undo toast with animation. */
  const hideUndoToast = useCallback(() => {
    Animated.timing(toastOpacity, {
      toValue: 0,
      duration: 200,
      useNativeDriver: false,
    }).start();
  }, [toastOpacity]);

  /**
   * Dismiss handler with undo mechanism.
   * Immediately hides card, shows 5s undo toast.
   * Only calls server after undo window expires.
   */
  const handleDismissWithUndo = useCallback(
    (listingId) => {
      // Clear any existing undo timer
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
        // Commit the previous dismiss immediately
        if (undoListingId) {
          handleDismiss(undoListingId);
        }
      }

      // Optimistically hide card
      setDismissedIds((prev) => new Set(prev).add(listingId));
      setUndoListingId(listingId);
      showUndoToast();

      // Start 5-second undo window
      undoTimerRef.current = setTimeout(() => {
        // Undo window expired — commit the dismiss to server
        handleDismiss(listingId);
        setUndoListingId(null);
        hideUndoToast();
        undoTimerRef.current = null;
      }, UNDO_TOAST_DURATION_MS);
    },
    [undoListingId, handleDismiss, showUndoToast, hideUndoToast]
  );

  /** Undo handler — re-shows the dismissed card without server call. */
  const handleUndo = useCallback(() => {
    if (!undoListingId) return;

    // Clear the timer
    if (undoTimerRef.current) {
      clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }

    // Re-show the card
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.delete(undoListingId);
      return next;
    });
    setUndoListingId(null);
    hideUndoToast();
  }, [undoListingId, hideUndoToast]);

  /** Save handler. */
  const handleSavePress = useCallback(
    (listingId) => {
      handleSave(listingId);
    },
    [handleSave]
  );

  /** Card tap handler — if card is active, navigates to listing detail; otherwise selects it. */
  const handleCardPress = useCallback(
    (listing) => {
      const idx = visibleListings.findIndex((l) => l.id === listing.id);
      if (idx !== -1 && idx !== activeCardIndex) {
        setActiveCardIndex(idx);
        return;
      }
      handleView(listing.id);
      router.push(`/listing/${listing.id}`);
    },
    [handleView, visibleListings, activeCardIndex, router]
  );

  /** Close popup handler. */
  const handleClosePopup = useCallback(() => {
    setSelectedListing(null);
  }, []);

  /** Notify me handler — opts user into email digest and shows confirmation. */
  const [notifyConfirmed, setNotifyConfirmed] = useState(false);

  // Check if user is already opted in on mount
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('users')
      .select('email_digest_opted_in')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.email_digest_opted_in) setNotifyConfirmed(true);
      });
  }, [user?.id]);

  const handleNotifyMe = useCallback(async () => {
    posthog?.capture('listing_notify_me', { model_id: modelId, vehicle_model: vehicleModel });
    setNotifyConfirmed(true);

    if (user?.id) {
      try {
        const { error: updateError } = await supabase
          .from('users')
          .update({ email_digest_opted_in: true })
          .eq('id', user.id);
        if (updateError) throw updateError;

        trackConversion(
          'email_alerts_subscribed',
          { user_id: user.id, source: 'notify_me' },
          { email: user.email }
        );
      } catch (err) {
        logger.error('[ListingsFeed] Failed to opt into email digest:', err);
      }
    }
  }, [posthog, modelId, vehicleModel, user?.id]);

  // Track current scroll offset for visibility calculations
  const scrollOffsetRef = useRef(0);
  const containerWidthRef = useRef(0);

  /** Navigate to previous card — only scrolls if the card is off-screen. */
  const handlePrevCard = useCallback(() => {
    if (activeCardIndex <= 0) return;
    const newIndex = activeCardIndex - 1;
    setActiveCardIndex(newIndex);

    // Scroll only if the new active card's left edge is before the visible area
    const cardLeft = newIndex * snapInterval;
    if (cardLeft < scrollOffsetRef.current) {
      flatListRef.current?.scrollToOffset({ offset: cardLeft, animated: true });
    }
  }, [activeCardIndex, snapInterval]);

  /** Navigate to next card — only scrolls if the card is off-screen. */
  const handleNextCard = useCallback(() => {
    if (activeCardIndex >= visibleListings.length - 1) return;
    const newIndex = activeCardIndex + 1;
    setActiveCardIndex(newIndex);

    // Scroll only if the new active card's right edge exceeds the visible area
    const cardRight = (newIndex + 1) * snapInterval - 16; // subtract gap
    const visibleRight = scrollOffsetRef.current + containerWidthRef.current;
    if (cardRight > visibleRight) {
      flatListRef.current?.scrollToOffset({
        offset: cardRight - containerWidthRef.current,
        animated: true,
      });
    }
  }, [activeCardIndex, snapInterval, visibleListings.length]);

  /** Track scroll offset for visibility calculations. */
  const handleScroll = useCallback((e) => {
    scrollOffsetRef.current = e.nativeEvent.contentOffset.x;
  }, []);

  /** Measure the visible container width. */
  const handleContainerLayout = useCallback((e) => {
    containerWidthRef.current = e.nativeEvent.layout.width;
  }, []);

  // ---- Render helpers ----

  const renderItem = useCallback(
    ({ item, index }) => {
      const isActive = index === activeCardIndex;
      const isLockedCard = isTrial && index >= TRIAL_UNLOCKED_COUNT;
      return (
        <View style={[styles.cardWrapper, isActive && styles.cardWrapperActive]}>
          <ListingCard
            listing={item}
            fitScore={fitScore}
            vehicleModelImageUrl={vehicleModelImageUrl}
            feedGeneratedAt={feedGeneratedAt}
            isSaving={savingIds.has(item.id)}
            isDismissing={dismissingIds.has(item.id)}
            isTopMatch={topMatchIds?.has(item.id) ?? false}
            isBestDeal={bestPriceIds?.has(item.id) ?? false}
            onPress={handleCardPress}
            onSave={handleSavePress}
            onDismiss={handleDismissWithUndo}
            isLocked={isLockedCard}
          />
        </View>
      );
    },
    [
      activeCardIndex,
      isTrial,
      fitScore,
      vehicleModelImageUrl,
      feedGeneratedAt,
      topMatchIds,
      bestPriceIds,
      savingIds,
      dismissingIds,
      handleCardPress,
      handleSavePress,
      handleDismissWithUndo,
    ]
  );

  const keyExtractor = useCallback((item) => item.id, []);

  const getItemLayout = useCallback(
    (_data, index) => ({
      length: snapInterval,
      offset: snapInterval * index,
      index,
    }),
    [snapInterval]
  );

  // ---- Loading skeleton ----
  if (isLoading && visibleListings.length === 0) {
    return (
      <View style={styles.container}>
        <FeedHeader vehicleModel={vehicleModel} listingCount={0} />
        <View style={styles.skeletonList}>
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <SkeletonCard key={`skeleton-${i}`} isMobile={isMobile} screenWidth={screenWidth} />
          ))}
        </View>
      </View>
    );
  }

  // ---- Empty state ----
  if (isEmpty) {
    return (
      <View style={styles.container}>
        <FeedHeader vehicleModel={vehicleModel} listingCount={0} />
        <EmptyState vehicleModel={vehicleModel} onNotifyMe={handleNotifyMe} notifyConfirmed={notifyConfirmed} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FeedHeader vehicleModel={vehicleModel} listingCount={visibleListings.length} />

      <View style={styles.feedListWrapper} onLayout={handleContainerLayout}>
        <FlatList
          ref={flatListRef}
          data={visibleListings}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.listContent}
          getItemLayout={getItemLayout}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          initialNumToRender={4}
          maxToRenderPerBatch={6}
          windowSize={5}
        />
        {visibleListings.length > 1 && (
          <View style={styles.feedNavRow}>
            <TouchableOpacity
              style={[styles.feedArrow, activeCardIndex === 0 && styles.feedArrowDisabled]}
              onPress={handlePrevCard}
              disabled={activeCardIndex === 0}
            >
              <Icon name="chevron-left" size={20} color={activeCardIndex === 0 ? 'rgba(255,255,255,0.3)' : '#FFFFFF'} />
            </TouchableOpacity>
            <Text style={styles.feedCounter}>
              {activeCardIndex + 1} / {visibleListings.length}
            </Text>
            <TouchableOpacity
              style={[styles.feedArrow, activeCardIndex >= visibleListings.length - 1 && styles.feedArrowDisabled]}
              onPress={handleNextCard}
              disabled={activeCardIndex >= visibleListings.length - 1}
            >
              <Icon name="chevron-right" size={20} color={activeCardIndex >= visibleListings.length - 1 ? 'rgba(255,255,255,0.3)' : '#FFFFFF'} />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* "N more on Pro" footer card: Free past FREE_DISPLAY_CAP, Trial past
          TRIAL_UNLOCKED_COUNT (matches the /home secondary unlock-all prompt). */}
      {((isTrial && cappedTotalCount > TRIAL_UNLOCKED_COUNT)
        || (!hasProAccess && !isTrial && cappedTotalCount > FREE_DISPLAY_CAP)) && (
        <FeedCapCTA totalCount={cappedTotalCount} />
      )}

      {/* No longer available listings */}
      {noLongerAvailableListings.length > 0 && (
        <View style={styles.unavailableSection}>
          <Text style={styles.unavailableSectionTitle}>No Longer Available</Text>
          <FlatList
            data={noLongerAvailableListings}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            horizontal
            showsHorizontalScrollIndicator={false}
            snapToInterval={snapInterval}
            snapToAlignment="start"
            decelerationRate="fast"
            contentContainerStyle={styles.listContent}
            getItemLayout={getItemLayout}
            initialNumToRender={3}
            maxToRenderPerBatch={4}
            windowSize={3}
          />
        </View>
      )}

      {/* Undo toast */}
      {undoListingId && (
        <Animated.View style={[styles.undoToastContainer, { opacity: toastOpacity }]}>
          <UndoToast onUndo={handleUndo} />
        </Animated.View>
      )}

      {/* Listing detail popup */}
      <ListingDetailPopup
        listing={selectedListing}
        visible={!!selectedListing}
        onClose={handleClosePopup}
        onSimilarPress={handleCardPress}
      />
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
  },

  // Header
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingHorizontal: 4,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: '#FFFFFF',
    lineHeight: 28,
  },
  headerCount: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
  },

  // Card wrappers
  cardWrapper: {
    marginRight: 16,
    transform: [{ scale: 0.95 }],
    transitionProperty: 'transform',
    transitionDuration: '200ms',
  },
  cardWrapperActive: {
    transform: [{ scale: 1 }],
  },

  // FlatList content
  feedListWrapper: {
    position: 'relative',
  },
  listContent: {
    paddingRight: 16,
    alignItems: 'center',
  },
  feedNavRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    marginTop: 12,
  },
  feedArrow: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  feedArrowDisabled: {
    opacity: 0.4,
  },
  feedCounter: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.55)',
    minWidth: 40,
    textAlign: 'center',
  },

  // Skeleton loading
  skeletonList: {
    flexDirection: 'row',
    overflow: 'hidden',
  },

  // Empty state
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    paddingHorizontal: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.08)',
  },
  emptyIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  emptyTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#FFFFFF',
    textAlign: 'center',
    marginBottom: 8,
    maxWidth: 320,
    lineHeight: 22,
  },
  emptySubtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.55)',
    textAlign: 'center',
    marginBottom: 20,
    maxWidth: 300,
    lineHeight: 20,
  },
  notifyMeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: COLORS.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
  },
  notifyMeText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: COLORS.white,
  },
  notifyConfirmed: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(22, 163, 74, 0.08)',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 10,
  },
  notifyConfirmedText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: '#16A34A',
  },

  // No longer available section
  unavailableSection: {
    marginTop: 20,
    opacity: 0.7,
  },
  unavailableSectionTitle: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.4)',
    marginBottom: 10,
    paddingHorizontal: 4,
  },

  // Undo toast
  undoToastContainer: {
    position: 'absolute',
    bottom: -8,
    left: 0,
    right: 0,
    alignItems: 'center',
    zIndex: 10,
  },
  undoToast: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#1A1A1A',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.16)',
  },
  undoToastText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: COLORS.white,
    flex: 1,
  },
  undoButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 8,
  },
  undoButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 13,
    color: COLORS.white,
  },

  // Refreshing overlay
  refreshingOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    marginBottom: 12,
    backgroundColor: 'rgba(20, 76, 184, 0.04)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(20, 76, 184, 0.12)',
  },
  refreshingText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: '#FFFFFF',
    marginTop: 12,
  },
  refreshingSubtext: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 13,
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: 4,
  },
});

export default memo(ListingsFeed);
